/** Quick Add (spec §6C) — floating "+" affordance opens a searchable
 * node-type palette, keyboard-friendly (arrow keys + Enter), so power
 * users never need the Left Library or a right-click. */

import { Input, Modal } from 'antd'
import { useMemo, useState, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import { getAuthMatrixRevision, subscribeAuthMatrix } from './authMatrixRuntime'
import { getAddNodeCatalogDefinitions } from './canvasNodeCatalog'
import { getNodeCatalogRevision, subscribeNodeCatalog } from './nodeCatalogRuntime'
import { useNodeIconRevision } from './nodeRegistry'
import { useCanvasNodeRegistryI18n } from './nodeRegistryI18n'

interface Props {
  open: boolean
  onClose: () => void
  onPick: (nodeType: string) => void
}

export default function QuickAddModal({ open, onClose, onPick }: Props) {
  const { t } = useTranslation('canvas')
  const { nodeLabel, nodeDescription } = useCanvasNodeRegistryI18n()
  const [query, setQuery] = useState('')
  const iconRev = useNodeIconRevision()
  const catalogRev = useSyncExternalStore(subscribeNodeCatalog, getNodeCatalogRevision, getNodeCatalogRevision)
  const authRev = useSyncExternalStore(subscribeAuthMatrix, getAuthMatrixRevision, getAuthMatrixRevision)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    const defs = getAddNodeCatalogDefinitions()
    if (!q) return [...defs]
    return defs.filter((def) => {
      const label = nodeLabel(def.nodeType, def.label).toLowerCase()
      const desc = nodeDescription(def.nodeType, def.description).toLowerCase()
      return label.includes(q) || desc.includes(q) || def.nodeType.toLowerCase().includes(q)
    })
  }, [nodeDescription, nodeLabel, query, iconRev, catalogRev, authRev])

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={t('quickAdd.title')}
      destroyOnHidden
      afterOpenChange={(visible) => {
        if (!visible) setQuery('')
      }}
    >
      <Input
        autoFocus
        placeholder={t('quickAdd.searchPlaceholder')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && filtered.length > 0) {
            onPick(filtered[0].nodeType)
          }
        }}
      />
      <div className="pcv-quickadd-list">
        {filtered.map((def) => (
          <button key={def.nodeType} type="button" className="pcv-quickadd-item" onClick={() => onPick(def.nodeType)}>
            <span className="pcv-quickadd-icon" style={{ color: def.color, background: `${def.color}22` }}>
              {def.icon}
            </span>
            <div className="pcv-quickadd-text">
              <div className="pcv-quickadd-label">{nodeLabel(def.nodeType, def.label)}</div>
              <div className="pcv-quickadd-desc">{nodeDescription(def.nodeType, def.description)}</div>
            </div>
          </button>
        ))}
        {filtered.length === 0 ? <div className="pcv-quickadd-empty">{t('quickAdd.empty')}</div> : null}
      </div>
    </Modal>
  )
}
